import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Activity, Bug, CreditCard, HardDrive, ArrowUpRight, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import { getSummary, getRecent, getTrends } from '../services/dashboardService';
import { formatRelativeTime, formatBytes } from '../utils/dateUtils';

const PLAN_BADGE: Record<string, 'info' | 'success' | 'default'> = { FREE: 'default', PRO: 'info', ENTERPRISE: 'success' };
const STATUS_BADGE: Record<string, 'error' | 'warning' | 'success'> = { OPEN: 'error', IN_PROGRESS: 'warning', RESOLVED: 'success' };
const PERIOD_OPTIONS = [
  { value: 7, label: '7일' },
  { value: 30, label: '30일' },
  { value: 90, label: '90일' },
  { value: 180, label: '180일' },
];
const PIE_COLORS: Record<string, string> = { FREE: '#9CA3AF', PRO: '#3B82F6', ENTERPRISE: '#8B5CF6' };

export default function DashboardPage() {
  const navigate = useNavigate();
  const [trendDays, setTrendDays] = useState(30);

  const { data: summary, isLoading: summaryLoading } = useQuery({ queryKey: ['admin', 'dashboard', 'summary'], queryFn: getSummary });
  const { data: recent, isLoading: recentLoading } = useQuery({ queryKey: ['admin', 'dashboard', 'recent'], queryFn: getRecent });
  const { data: trends, isLoading: trendsLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'trends', trendDays],
    queryFn: () => getTrends({ days: trendDays }),
  });

  // 차트 데이터 변환
  const chartData = trends ? trends.labels.map((label, i) => ({
    label,
    orgs: trends.datasets.newOrganizations[i] ?? 0,
    users: trends.datasets.newUsers[i] ?? 0,
    bugs: trends.datasets.bugReports[i] ?? 0,
  })) : [];

  const pieData = trends ? Object.entries(trends.datasets.planDistribution).map(([name, value]) => ({
    name,
    value: Array.isArray(value) ? value[value.length - 1] ?? 0 : value,
  })) : [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">플랫폼 대시보드</h1>

      {/* ====== 요약 카드 ====== */}
      {summaryLoading ? (
        <div className="flex justify-center py-12"><Spinner className="size-8" /></div>
      ) : summary ? (
        <div className="grid grid-cols-3 gap-6">
          <StatCard icon={Building2} label="총 조직 수" value={summary.totalOrganizations}
            delta={summary.organizationsDelta} deltaLabel="전주 대비" />
          <StatCard icon={Users} label="총 사용자 수" value={summary.totalUsers}
            delta={summary.usersDelta} deltaLabel="전주 대비" />
          <StatCard icon={Activity} label="활성 조직" value={summary.activeOrganizations}
            sub={`전체의 ${summary.activeOrganizationRate}%`} subColor="text-green-600" />
          <StatCard icon={Bug} label="미해결 오류" value={summary.openBugReports}
            delta={summary.openBugReportsDelta} deltaLabel="전일 대비" invertColor />
          <StatCard icon={CreditCard} label="Free / Pro 비율"
            value={`${summary.planDistribution?.FREE ?? 0} / ${(summary.planDistribution?.PRO ?? 0) + (summary.planDistribution?.ENTERPRISE ?? 0)}`}
            sub={`유료 전환율 ${summary.conversionRate}%`} subColor="text-blue-600" />
          <StatCard icon={HardDrive} label="총 스토리지" value={formatBytes(summary.totalStorageUsed)}
            sub={`한도의 ${summary.storageUsageRate}% (${formatBytes(summary.totalStorageLimit)})`}
            subColor={summary.storageUsageRate > 80 ? 'text-orange-600' : 'text-gray-500'} />
        </div>
      ) : null}

      {/* ====== 추이 차트 ====== */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">추이 차트</h2>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {PERIOD_OPTIONS.map((opt) => (
              <button key={opt.value} onClick={() => setTrendDays(opt.value)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  trendDays === opt.value ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}>{opt.label}</button>
            ))}
          </div>
        </div>

        {trendsLoading ? (
          <div className="flex justify-center py-12"><Spinner className="size-8" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {/* 신규 조직 */}
            <ChartCard title="신규 조직 가입 추이">
              <ResponsiveContainer width="100%" height={256}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="orgs" name="조직" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 신규 사용자 */}
            <ChartCard title="신규 사용자 가입 추이">
              <ResponsiveContainer width="100%" height={256}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="users" name="사용자" stroke="#10B981" fill="#10B981" fillOpacity={0.1} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 오류 보고 */}
            <ChartCard title="오류 보고 접수 추이">
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="bugs" name="오류 보고" fill="#EF4444" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 플랜 분포 */}
            <ChartCard title="플랜별 조직 분포">
              <ResponsiveContainer width="100%" height={256}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {pieData.map((entry) => (
                      <Cell key={entry.name} fill={PIE_COLORS[entry.name] ?? '#9CA3AF'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              {pieData.length > 0 && (
                <div className="flex justify-center gap-4 mt-2">
                  {pieData.map((d) => (
                    <div key={d.name} className="flex items-center gap-1.5 text-sm text-gray-500">
                      <div className="size-2.5 rounded-full" style={{ background: PIE_COLORS[d.name] }} />
                      {d.name} ({d.value})
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>
          </div>
        )}
      </div>

      {/* ====== 최근 활동 피드 ====== */}
      {recentLoading ? (
        <div className="flex justify-center py-8"><Spinner className="size-6" /></div>
      ) : recent ? (
        <div className="grid grid-cols-3 gap-6">
          <FeedCard title="최근 가입 조직">
            {recent.recentOrganizations.length === 0 ? <EmptyFeed /> :
              recent.recentOrganizations.map((org) => (
                <FeedItem key={org.id} onClick={() => navigate(`/organizations/${org.id}`)}
                  primary={org.name} secondary={formatRelativeTime(org.createdAt)}
                  badge={<Badge variant={PLAN_BADGE[org.planType] ?? 'default'}>{org.planType}</Badge>} />
              ))}
          </FeedCard>
          <FeedCard title="최근 오류 보고">
            {recent.recentBugReports.length === 0 ? <EmptyFeed /> :
              recent.recentBugReports.map((bug) => (
                <FeedItem key={bug.id} onClick={() => navigate(`/bug-reports/${bug.id}`)}
                  primary={bug.title} secondary={`${bug.organization?.name ?? '-'} / ${formatRelativeTime(bug.createdAt)}`}
                  badge={<Badge variant={STATUS_BADGE[bug.status] ?? 'default'}>{bug.status}</Badge>} />
              ))}
          </FeedCard>
          <FeedCard title="최근 유료 전환">
            {recent.recentProConversions.length === 0 ? <EmptyFeed /> :
              recent.recentProConversions.map((org) => (
                <FeedItem key={org.id} onClick={() => navigate(`/organizations/${org.id}`)}
                  primary={org.name} secondary={org.planStartedAt ? formatRelativeTime(org.planStartedAt) : '-'}
                  badge={<Badge variant="info">{org.planType}</Badge>} />
              ))}
          </FeedCard>
        </div>
      ) : null}
    </div>
  );
}

// --- Sub components ---

function DeltaIndicator({ delta, label, invertColor }: { delta: number; label: string; invertColor?: boolean }) {
  if (delta === 0) return <span className="text-sm text-gray-400 flex items-center gap-1"><Minus className="size-3" /> {label} 변동 없음</span>;
  const isPositive = delta > 0;
  const color = invertColor
    ? (isPositive ? 'text-red-600' : 'text-green-600')
    : (isPositive ? 'text-green-600' : 'text-red-600');
  const Icon = isPositive ? TrendingUp : TrendingDown;
  return (
    <span className={`text-sm flex items-center gap-1 ${color}`}>
      <Icon className="size-3" />
      {label} {isPositive ? '+' : ''}{delta}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, subColor = 'text-gray-500', delta, deltaLabel, invertColor }: {
  icon: typeof Building2; label: string; value: string | number;
  sub?: string; subColor?: string; delta?: number; deltaLabel?: string; invertColor?: boolean;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="size-5 text-gray-400" />
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {delta !== undefined && deltaLabel ? (
        <div className="mt-1"><DeltaIndicator delta={delta} label={deltaLabel} invertColor={invertColor} /></div>
      ) : sub ? (
        <p className={`text-sm mt-1 ${subColor}`}>{sub}</p>
      ) : null}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-medium text-gray-700 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function FeedCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-gray-900">{title}</h3></div>
      <div className="divide-y divide-gray-50">{children}</div>
    </div>
  );
}

function FeedItem({ primary, secondary, badge, onClick }: { primary: string; secondary: string; badge?: React.ReactNode; onClick?: () => void }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors text-left group">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-primary-600">{primary}</p>
        <p className="text-sm text-gray-400 mt-0.5">{secondary}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-3">{badge}<ArrowUpRight className="size-3.5 text-gray-300 group-hover:text-primary-500" /></div>
    </button>
  );
}

function EmptyFeed() {
  return <p className="px-5 py-6 text-center text-sm text-gray-400">항목이 없습니다</p>;
}
